import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { getConferenceName, getConferenceTimestamp } from '../../../base/conference/functions';
import { getLocalizedDurationFormatter } from '../../../base/i18n/dateUtil';
import Icon from '../../../base/icons/components/Icon';
import { IconInfoCircle } from '../../../base/icons/svg';
import logger from '../../logger';
/** 会议详情按钮图标尺寸（px）。 */
const MEETING_INFO_ICON_SIZE_PX = 16;
/** 时长刷新间隔（毫秒）。 */
const MEETING_DURATION_TICK_MS = 1000;
/** 详情浮层空值占位。 */
const MEETING_DETAILS_EMPTY_VALUE = '—';
const useStyles = makeStyles()(theme => {
    return {
        wrap: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%',
            flexShrink: 0
        },
        button: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: '28px',
            padding: '0 8px',
            marginLeft: '2px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: theme.palette.conferenceSubjectText,
            cursor: 'pointer',
            ...theme.typography.labelRegular,
            boxSizing: 'border-box',
            flexShrink: 0,
            '&:hover': {
                backgroundColor: theme.palette.ui03
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '1px'
            },
            '@media (max-width: 300px)': {
                display: 'none'
            }
        },
        label: {
            whiteSpace: 'nowrap'
        },
        popover: {
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: '220px',
            maxWidth: '320px',
            padding: '12px 14px',
            backgroundColor: theme.palette.ui01,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
            zIndex: 100,
            color: theme.palette.text01,
            boxSizing: 'border-box'
        },
        row: {
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            marginBottom: '8px',
            '&:last-child': {
                marginBottom: 0
            }
        },
        rowLabel: {
            ...theme.typography.labelBold,
            color: theme.palette.text03,
            fontSize: '11px'
        },
        rowValue: {
            ...theme.typography.labelRegular,
            color: theme.palette.text01,
            wordBreak: 'break-all'
        }
    };
});
/**
 * 会议顶栏左上角「会议详情」：在左上角展开浮层展示会议室/会议号/时长，
 * 与 Electron 客户端行为一致，不打开会议邀请弹窗。
 *
 * @returns {JSX.Element}
 */
const MeetingInfoLabel = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const wrapRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [durationText, setDurationText] = useState(getLocalizedDurationFormatter(0));
    const conferenceName = useSelector(getConferenceName);
    const room = useSelector((state) => state['features/base/conference'].room || '');
    const startTimestamp = useSelector(getConferenceTimestamp);
    useEffect(() => {
        if (!startTimestamp) {
            setDurationText(getLocalizedDurationFormatter(0));
            return;
        }
        const tick = () => {
            const elapsedMs = Math.max(0, Date.now() - startTimestamp);
            setDurationText(getLocalizedDurationFormatter(elapsedMs));
        };
        tick();
        const timerId = window.setInterval(tick, MEETING_DURATION_TICK_MS);
        return () => window.clearInterval(timerId);
    }, [startTimestamp]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const onMouseDown = (event) => {
            if (!wrapRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);
    const onToggle = useCallback(() => {
        setOpen(prev => {
            const next = !prev;
            sendAnalytics(createToolbarEvent('meeting.info', { open: next }));
            logger.info(`[会议详情] ${new Date().toISOString()} 切换详情浮层 | open=${next}`
                + ` room=${room || '-'} name=${conferenceName || '-'}`);
            return next;
        });
    }, [conferenceName, room]);
    const displayRoom = conferenceName || room || MEETING_DETAILS_EMPTY_VALUE;
    const displayMeetingId = room && room !== conferenceName ? room : '';
    return (React.createElement("div", { className: classes.wrap, ref: wrapRef },
        React.createElement("button", { "aria-expanded": open, "aria-label": t('info.meetingInfo'), className: classes.button, onClick: onToggle, title: t('info.meetingInfoTooltip'), type: 'button' },
            React.createElement(Icon, { size: MEETING_INFO_ICON_SIZE_PX, src: IconInfoCircle }),
            React.createElement("span", { className: classes.label }, t('info.meetingInfo'))),
        open && (React.createElement("div", { className: classes.popover, role: 'dialog' },
            React.createElement("div", { className: classes.row },
                React.createElement("span", { className: classes.rowLabel }, t('conferenceHeader.meetingRoomLabel')),
                React.createElement("span", { className: classes.rowValue }, displayRoom)),
            displayMeetingId ? (React.createElement("div", { className: classes.row },
                React.createElement("span", { className: classes.rowLabel }, t('conferenceHeader.meetingIdLabel')),
                React.createElement("span", { className: classes.rowValue }, displayMeetingId))) : null,
            React.createElement("div", { className: classes.row },
                React.createElement("span", { className: classes.rowLabel }, t('conferenceHeader.meetingDurationLabel')),
                React.createElement("span", { className: classes.rowValue }, durationText))))));
};
export default MeetingInfoLabel;
